import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { AddMultipleComponent, IAddedItem } from 'components/input/add-multiple/add-multiple.component';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { CustomSelectComponent } from 'components/input/custom-select/custom-select.component';
import { DataRowComponent, DataTableComponent } from 'components/layout/data-table/data-table.component';
import { InputType } from 'enums/InputType';
import { ValidationType } from 'enums/validationType';
import 'extensions/validation.ts';
import { CommunicationLogEntryModel } from 'models/generated/Communication.Api.Models.CommunicationLogEntry';
import { LogEntryMailModel } from 'models/generated/Communication.Api.Models.LogEntryAdditionalData.LogEntryMail';
import { MailTemplateModel } from 'models/generated/Communication.Api.Models.MailTemplate';
import { ContactModel } from 'models/generated/Entities.GormCrm.Contact';
import { TaskActionModel } from 'models/generated/Entities.GormCrm.TaskAction';
import { SelectListItemModel } from 'models/SelectList';
import { Layout } from 'modules/layout';
import { TranslatePipe } from 'pipes/translate.pipe';
import { FormValidationService } from 'services/form-validation.service';
import { MailService } from 'services/mail.service';
import { UserService } from 'services/user.service';

@Component({
	selector: 'task-action-mail',
	template: '<section><task-action-mail-input #mailInput [taskAction]="taskAction" [contacts]="contacts" [logEntryMail]="logEntryMail"></task-action-mail-input></section>' +
		'<section>' +
		'	<button ghost (click)="onCancel.emit()" [ignoreValidation]="true">{{ \'general.cancel\' | translate }}</button>' +
		'	<button confirm (click)="onSave.emit(mailInput.getMail())">{{ \'communication-log.send-mail\' | translate }}</button>' +
		'</section>',
	providers: [FormValidationService],
	imports: [Layout, forwardRef(() => TaskActionMailInputComponent), TranslatePipe]
})
export class TaskActionMailComponent {

	@Input() taskAction?: TaskActionModel;
	@Input() contacts: ContactModel[] = [];
	@Input() logEntryMail?: CommunicationLogEntryModel;
	@Output() onCancel = new EventEmitter<void>();
	@Output() onSave = new EventEmitter<{ comment: string, subject: string, recipients: string[]; }>();

	mailInput!: TaskActionMailInputComponent;
}
@Component({
	selector: 'task-action-mail-input',
	templateUrl: './task-action-mail.component.html',
	imports: [CustomSelectComponent, AddMultipleComponent, CustomInputComponent, TranslatePipe]
})
export class TaskActionMailInputComponent {

	recipients: string[] = [];
	mailTemplates: MailTemplateModel[] = [];
	mailTemplatesList: SelectListItemModel[] = [];
	signature = '';
	contactMails: IAddedItem[] = [];
	signatureLoaded = false;
	isMailReply = false;
	sourceMail = '';

	inputTypeEnum = InputType;
	validationTypeEnum = ValidationType;
	mailData: { comment: string, subject: string, recipients: string[]; } = { comment: '', subject: '', recipients: [] };

	@Input() taskAction!: TaskActionModel;
	@Input()
	set contacts(value: ContactModel[]) {
		if (!value) {
			this.contactMails = [];
			return;
		}
		this.contactMails = value.filter(v => v.emailAddress != null && v.emailAddress.isValidEmailAddress()).map((contact) => {
			return { value: contact.emailAddress, label: contact.fullName + '(' + contact.emailAddress + ')' };
		});
	}

	@Input()
	set logEntryMail(value: CommunicationLogEntryModel) {
		if (!value) return;
		this.isMailReply = true;

		const logEntryMail = this.entryAdditionalDataAsLogEntryModel(value);

		this.mailData.recipients.push(logEntryMail.sender);
		this.mailData.subject = logEntryMail.subject;

		this.sourceMail = '<hr>';
		this.sourceMail += '<b>' + this.translate.transform('general.mail.sender') + ': </b>' + logEntryMail.sender + '<br>';
		this.sourceMail += '<b>' + this.translate.transform('general.mail.recipients') + ': </b>';
		logEntryMail.recipients.forEach((recipient: string) => { this.sourceMail += recipient + '; '; });
		this.sourceMail += '<br>';
		this.sourceMail += '<b>' + this.translate.transform('general.mail.subject') + ': </b>' + logEntryMail.subject + '<br>';
		this.sourceMail += '<b>' + value.comment + '</b>';
	}

	constructor(private translate: TranslatePipe,
		private userService: UserService,
		private mailService: MailService) {

		this.signature = this.translate.transform('general.user-mail-signature', { 'user.name': this.userService.getCurrentUser().name });
		this.appendSignature();
		this.signatureLoaded = true;

		this.mailService.getMailTemplates().then(templates => {
			this.mailTemplates = templates;
			for (let template of templates) {
				this.mailTemplatesList.push(new SelectListItemModel(template.id!, template.title));
			}
		});
	}

	recipientsChanged(addedAddresses: IAddedItem[]) {
		this.mailData.recipients = addedAddresses.map(item => item.value);
	}

	getMail(): { comment: string, subject: string, recipients: string[]; } {
		if (this.isMailReply) {
			this.mailData.comment += this.sourceMail;
		}
		return this.mailData;
	}

	getTrackingCode() {
		return '[Track#' + this.taskAction?.trackingId + ']';
	}

	changeMailTemplate(templateId: string) {
		let mailTemplate = this.mailTemplates.find(t => t.id === templateId);
		if (mailTemplate) {
			this.mailData.comment = mailTemplate.content;
			this.appendSignature();
			this.mailData.subject = mailTemplate.subject;
		}
	}

	appendSignature() {
		if (this.mailData.comment == '')
			this.mailData.comment = '<p></p>';
		this.mailData.comment += this.signature;
	}

	entryAdditionalDataAsLogEntryModel(entry: CommunicationLogEntryModel | undefined) : LogEntryMailModel {
		return entry && entry.additionalData ? JSON.parse(entry.additionalData) : new LogEntryMailModel();
	}
}
